import { useTranslation } from 'react-i18next'
import { ArrowTriangleIcon } from '@honestica/curecall-ui'
import { twMerge } from 'src/utils/tw-merge.util'

interface PaginationPreviousButtonProps {
  currentPage: number
  onChangePage: (page: number) => void
}
const PaginationPreviousButton = ({ currentPage, onChangePage }: Readonly<PaginationPreviousButtonProps>) => {
  const { t } = useTranslation()

  const isDisabled = currentPage <= 1
  const onClick = () => onChangePage(currentPage - 1)
  return (
    <button
      type="button"
      className="c-text-primary c-font-bold disabled:c-text-grey-400 hover:c-text-primary-500 c-group"
      disabled={isDisabled}
      onClick={onClick}
    >
      {t('pages.planning.pagination.previous')}
      <ArrowTriangleIcon
        position="LEFT"
        className={twMerge(
          'c-w-4 c-text-primary c-inline-block c-ml-1 group-hover:c-text-primary-500',
          isDisabled && 'c-text-grey-400 group-hover:c-text-grey-400',
        )}
      />
    </button>
  )
}

interface PaginationNextButtonProps {
  currentPage: number
  totalPages: number
  onChangePage: (page: number) => void
}
const PaginationNextButton = ({
  currentPage,
  totalPages,
  onChangePage,
}: Readonly<PaginationNextButtonProps>) => {
  const { t } = useTranslation()

  const isDisabled = currentPage >= totalPages
  const onClick = () => onChangePage(currentPage + 1)

  return (
    <button
      type="button"
      className="c-text-primary c-font-bold disabled:c-text-grey-400 hover:c-text-primary-500 c-group"
      disabled={isDisabled}
      onClick={onClick}
    >
      <ArrowTriangleIcon
        position="RIGHT"
        className={twMerge(
          'c-w-4 c-text-primary c-inline-block c-mr-1 group-hover:c-text-primary-500',
          isDisabled && 'c-text-grey-400 group-hover:c-text-grey-400',
        )}
      />
      {t('pages.planning.pagination.next')}
    </button>
  )
}

interface PaginationProps {
  currentPage: number
  totalPages: number
  onChangePage: (page: number) => void
}
const SmallPagination = ({ currentPage, totalPages, onChangePage }: Readonly<PaginationProps>) => {
  return (
    <nav className="c-flex c-items-center c-gap-4">
      <PaginationPreviousButton currentPage={currentPage} onChangePage={onChangePage} />
      <div className="c-flex c-items-center c-gap-1">
        {Array.from({ length: totalPages }, (_, index) => {
          const pageNumber = index + 1
          const isCurrentPage = pageNumber === currentPage

          return (
            <button
              key={pageNumber}
              type="button"
              className={twMerge(
                'c-px-2.5 c-py-1 c-rounded-full c-font-bold c-transition-colors c-text-primary hover:c-bg-primary/10',
                isCurrentPage && 'c-bg-primary c-text-white hover:c-bg-primary-700',
              )}
              onClick={() => onChangePage(pageNumber)}
            >
              {pageNumber}
            </button>
          )
        })}
      </div>
      <PaginationNextButton currentPage={currentPage} totalPages={totalPages} onChangePage={onChangePage} />
    </nav>
  )
}

const LargePagination = ({ currentPage, totalPages, onChangePage }: Readonly<PaginationProps>) => {
  const generatePageNumbers = () => {
    const pages: (number | string)[] = []

    // Always show the first page
    pages.push(1)

    // If we are far from the beginning, add ellipses
    if (currentPage > 4) {
      pages.push('...')
    }

    // Pages around the current page
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)

    for (let i = start; i <= end; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i)
      }
    }

    // If we are far from the end, add ellipses
    if (currentPage < totalPages - 3) {
      pages.push('...')
    }

    // Always show the last page (if it's different from the first)
    if (totalPages > 1) {
      pages.push(totalPages)
    }

    return pages
  }

  const pageNumbers = generatePageNumbers()

  return (
    <nav className="c-flex c-items-center c-gap-4">
      <PaginationPreviousButton currentPage={currentPage} onChangePage={onChangePage} />

      <div className="c-flex c-items-center c-gap-1">
        {pageNumbers.map((pageNumber, index) => {
          if (pageNumber === '...') {
            return (
              <span key={`ellipsis-${index}`} className="c-px-2.5 c-py-1 c-text-grey-600">
                {'...'}
              </span>
            )
          }

          return (
            <button
              key={pageNumber}
              type="button"
              className={twMerge(
                'c-px-2.5 c-py-1 c-rounded-full c-font-bold c-transition-colors c-text-primary hover:c-bg-primary/10',
                pageNumber === currentPage && 'c-bg-primary c-text-white hover:c-bg-primary-700',
              )}
              onClick={() => onChangePage(pageNumber as number)}
            >
              {pageNumber}
            </button>
          )
        })}
      </div>

      <PaginationNextButton currentPage={currentPage} totalPages={totalPages} onChangePage={onChangePage} />
    </nav>
  )
}

export { PaginationPreviousButton, PaginationNextButton, SmallPagination, LargePagination }
